import React, { useEffect, useState, useCallback } from "react";

import { NavLink } from "react-router-dom";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow_down.svg";
import { ReactComponent as AddIcon } from "../../../assets/icons/add.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/delete.svg";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";

import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";

import { useDataset } from "../../../Api/newSockets.api";
import dataApi, { Dataset as DatasetType, DatasetImage } from "../../../Api/data.api";
import Table from "../../../components/Table/Table";
import AddButton from "../../../components/Buttons/AddButton";
import AddImagesWizard from "../../../components/Modals/AddImagesWizard";
import ActionButton from "../../../components/Buttons/ActionButton";
import { toast, ToastContainer } from "react-toastify";
import Modal from "../../../components/Modals/Modal";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import OutlinedButton from "../../../components/Buttons/OutlinedButton";
import { savePathToLocalStorage } from "../../../utils/helpers";
import AnnotationTool from "../../../components/Annotation/AnnotationTool";
import useCheckMobileScreen from "../../../hooks/useCheckScreen";
import DesktopOnlyMessage from "../../../components/DesktopOnlyMessage/DesktopOnlyMessage";
import { MetricsExplorer } from "./MetricsExplorer";
import "../../../styles/mes.scss";

const Dataset = (props) => {
  savePathToLocalStorage();

  const datasetId = parseInt(props.match.params.id);
  const {
    uploadImage,
    deleteImage,
    addAnnotation,
  } = useDataset();

  const [dataset, setDataset] = useState<DatasetType | null>(null);
  const [images, setImages] = useState<DatasetImage[]>([]);
  const [imagesTotal, setImagesTotal] = useState(0);
  const [imagesPage, setImagesPage] = useState(0);
  const [imagesLoading, setImagesLoading] = useState(false);

  const PAGE_SIZE = 50;

  const [errorModal, setErrorModal] = useState(false);
  const [row, setRow] = useState({ fileName: "" });
  const [wizardState, setWizardState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const currentUserDevice = useCheckMobileScreen();

  const [annotationMode, setAnnotationMode] = useState<boolean>(false);
  const [imageIndex, setImageIndex] = useState<number | undefined>();

  // Load dataset metadata
  useEffect(() => {
    dataApi.getDataset(datasetId)
      .then((data) => setDataset(data))
      .catch((err) => console.error('Failed to load dataset:', err));
  }, [datasetId]);

  // Load images (paginated) for Images datasets
  const loadImages = useCallback(async (page: number) => {
    if (!dataset || dataset.type !== "Images") return;
    setImagesLoading(true);
    try {
      const result = await dataApi.getDatasetImages(datasetId, page * PAGE_SIZE, PAGE_SIZE);
      setImages(result.data);
      setImagesTotal(result.totalCount);
      setImagesPage(page);
    } catch (err) {
      console.error('Failed to load images:', err);
    } finally {
      setImagesLoading(false);
    }
  }, [datasetId, dataset]);

  // Initial load for images when dataset type is Images
  useEffect(() => {
    if (!dataset) return;
    if (dataset.type === "Images" && images.length === 0) {
      loadImages(0);
    }
  }, [dataset, loadImages]);

  const headers = [
    {
      label: "Image",
      key: "thumbnailUrl",
      type: "image",
    },
    {
      label: "Name",
      key: "name",
      type: "text",
    },
    {
      label: "File Name",
      key: "fileName",
      type: "text",
    },
    {
      label: "URL",
      key: "url",
      type: "text",
    },
    {
      label: "Date created",
      key: "dateCreated",
      type: "date",
    },
  ];

  const handleSaveButton = (uploadedImages) => {
    setLoadingScreen(true);
    uploadImage(uploadedImages, dataset.id)
      .then((data: any) => {
        console.log(data);
      })
      .then(() => {
        // Reload dataset metadata and images
        return Promise.all([
          dataApi.getDataset(datasetId).then(setDataset),
          loadImages(0) // Go back to first page to see new images
        ]);
      })
      .then(() => setWizardState(false))
      .then(() => setLoadingScreen(false))
      .catch((error) => {
        toasti(error);
      });
  };

  const handleDeleteDataset = (row) => {
    setLoading(true);
    deleteImage(row.id)
      .then((data: any) => {
        console.log(data);
        // Reload dataset metadata and images
        return Promise.all([
          dataApi.getDataset(datasetId).then(setDataset),
          loadImages(imagesPage)
        ]);
      })
      .then(() => setLoading(false))
      .then(() => setErrorModal(false))
      .catch((error) => {
        toasti(error);
        console.log(error);
      });
  };

  const openWizard = () => {
    setWizardState(true);
  };

  const onClose = (info) => {
    if (info == "error") {
      setErrorModal(false);
    }
  };

  const onAddAnnotation = (imageIndex, annotations) => {
    console.log(
      "adding annotation for",
      imageIndex,
      ", annotations",
      annotations
    );
    addAnnotation({
      datasetId: datasetId,
      annotation: {
        imageId: images[imageIndex].id,
        annotations: annotations,
      },
    }).then((data: any) => {
      console.log("Annotation added successfully");
      // Reload images to get updated annotations
      loadImages(imagesPage);
    });
  };

  // Delete modal footer
  var footer = (
    <>
      <PrimaryButton
        className="delete-button-background-color"
        onSubmit={() => handleDeleteDataset(row)}
        loading={loading}
      >
        Delete
      </PrimaryButton>
      <OutlinedButton onClick={() => onClose("error")}>Cancel</OutlinedButton>
    </>
  );
  const openDeleteModal = (e, row) => {
    //Open delete modal
    setErrorModal(true);
    setRow(row);
    e.stopPropagation();
    e.preventDefault();
  };

  const actions = (row: any) => {
    return (
      <div>
        <ActionButton
          icon={<DeleteIcon />}
          className="list-item-action"
          onClick={(e) => openDeleteModal(e, row)}
        />
        <ActionButton
          icon={<EditIcon />}
          className="list-item-action"
          onClick={(e) => {
            setImageIndex(images.indexOf(row));
            setAnnotationMode(true);
          }}
        />
      </div>
    );
  };

  // Toastify handle error
  const ErrorMessage = ({ error }) => <div>{error}</div>;
  const toasti = (error) => {
    toast.error(<ErrorMessage error={error} />, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const isImagesDataset = dataset?.type === "Images";

  return dataset ? (
    <div className="dataset-main develop-main">
      <div className="header">
        <NavLink to="/data/datasets" className="device-actions">
          <ArrowDownIcon />
        </NavLink>
        <h3 className="title">{dataset.name}</h3>
        <div className="dataset-add-button-container">
          {isImagesDataset && (
            <AddButton
              icon={<AddIcon />}
              label="Add images"
              onClick={openWizard}
            />
          )}
        </div>
      </div>

      <section
        className="main-content develop"
        style={{ height: currentUserDevice && "100vw" }}
      >
        {isImagesDataset ? (
          !annotationMode ? (
            <>
              {imagesLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <Table
                    items={images}
                    headers={headers}
                    tableName="images"
                    actions={actions}
                  />
                  {/* Pagination controls */}
                  {imagesTotal > PAGE_SIZE && (() => {
                    const totalPages = Math.ceil(imagesTotal / PAGE_SIZE);
                    const btnStyle = (active: boolean, disabled?: boolean): React.CSSProperties => ({
                      minWidth: "2rem",
                      padding: "0.4rem 0.6rem",
                      borderRadius: "4px",
                      border: `1px solid ${active ? "#53bde4" : "rgba(108,117,125,0.5)"}`,
                      background: active ? "rgba(83,189,228,0.25)" : disabled ? "rgba(33,40,44,0.5)" : "transparent",
                      color: disabled ? "#6c757d" : active ? "#53bde4" : "#c5c5c5",
                      cursor: disabled ? "not-allowed" : "pointer",
                      fontWeight: active ? 700 : 400,
                    });

                    const pages: (number | "...")[] = [];
                    if (totalPages <= 7) {
                      for (let i = 0; i < totalPages; i++) pages.push(i);
                    } else {
                      pages.push(0);
                      if (imagesPage > 2) pages.push("...");
                      for (let i = Math.max(1, imagesPage - 1); i <= Math.min(totalPages - 2, imagesPage + 1); i++) pages.push(i);
                      if (imagesPage < totalPages - 3) pages.push("...");
                      pages.push(totalPages - 1);
                    }

                    return (
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.35rem", padding: "1rem", marginTop: "1rem", flexWrap: "wrap" }}>
                        <button onClick={() => loadImages(imagesPage - 1)} disabled={imagesPage === 0} style={btnStyle(false, imagesPage === 0)}>‹</button>
                        {pages.map((p, i) =>
                          p === "..."
                            ? <span key={`ellipsis-${i}`} style={{ color: "#6c757d", padding: "0 0.25rem" }}>…</span>
                            : <button key={p} onClick={() => loadImages(p as number)} style={btnStyle(p === imagesPage)}>{(p as number) + 1}</button>
                        )}
                        <button onClick={() => loadImages(imagesPage + 1)} disabled={(imagesPage + 1) * PAGE_SIZE >= imagesTotal} style={btnStyle(false, (imagesPage + 1) * PAGE_SIZE >= imagesTotal)}>›</button>
                        <span style={{ color: "#6c757d", fontSize: "0.8rem", marginLeft: "0.5rem" }}>{imagesTotal} images</span>
                      </div>
                    );
                  })()}
                </>
              )}
            </>
          ) : (
            <>
              {currentUserDevice ? (
                <DesktopOnlyMessage />
              ) : (
                <AnnotationTool
                  images={images}
                  imageIndex={imageIndex}
                  onAddAnnotation={onAddAnnotation}
                />
              )}
            </>
          )
        ) : (
          <div style={{ width: "100%", overflow: "auto" }}>
            <MetricsExplorer
              datasetId={datasetId}
              datasetName={dataset.name}
            />
          </div>
        )}
        {isImagesDataset && wizardState && (
          <AddImagesWizard
            datasetId={datasetId}
            setWizardState={setWizardState}
            onSave={handleSaveButton}
            loadingScreen={loadingScreen}
          />
        )}
      </section>
      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      {/* Delete modal */}
      <Modal
        title={`Delete ${row && row.fileName} ?`}
        subtitle={"Warning: this can't be undone"}
        desc="By deleting this image, you wont be able to return it back"
        visible={errorModal}
        footer={footer}
        onClose={() => onClose("error")}
      />
    </div>
  ) : (
    <LoadingSpinner />
  );
};

export default Dataset;
